import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Ban, Check, Circle, Clock, ExternalLink, FileText, Info, Phone, Play, RefreshCw, SkipForward, X } from 'lucide-react';
import type { Action, Atelier, CascadeEtape, ContexteUrgence, ContactUrgence, Demande, GroupeContacts, Personne, ResultatAppel } from '../../types';
import { formatDateLong, formatHoraire } from '../annuaire/format';
import { PoleDot } from '../demande/PoleDot';
import { BoutonCopier } from '../messages/BoutonCopier';
import {
  LIBELLE_COURT, LIBELLE_RESULTAT, accepteEnAttente, appelDe, disponibles, duree, estGroupeDeContacts, etapeCourante, ilYa, prochainContact, retardEscalade,
} from './regles';

// Le tableau de travail d'UNE urgence (DECISIONS §29) : le prochain contact à appeler, les contacts avec leur disponibilité, où en est la cascade, le journal, les issues.

const heure = (iso: string) => new Date(iso).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' }).replace(/^0/, '').replace(':', ' h ').replace(/ h 00$/, ' h');
const prenom = (n: string) => n.trim().split(/\s+/)[0] ?? n;

export interface PropsTableau {
  demande: Demande;
  atelier: Atelier | null;
  contexte: ContexteUrgence | null;
  contexteIndisponible: boolean;
  now: number;
  seuilMinutes: number;
  /** Les responsables réellement prévenu·e·s, quand l'urgence vient d'être déclarée ici. */
  confirmation: string[] | null;
  /** Quelqu'un d'autre a modifié l'urgence depuis le dernier chargement. */
  distant: { par: string | null } | null;
  /** Un geste refusé parce que quelqu'un d'autre venait de modifier l'urgence. */
  conflit: string | null;
  personnes: Personne[];
  hasFra: boolean;
  onAppel: (cle: string, c: ContactUrgence, r: ResultatAppel) => void;
  onCorriger: (cle: string, personneId: number) => void;
  onPasser: (statut: 'echec' | 'saute') => void;
  onCommentaire: (cle: string, texte: string) => void;
  onConfirmerRemplacement: (personneId: number) => void;
  onIssue: (k: 'fusion' | 'maintien' | 'annulation') => void;
  onActualiser: () => void;
  onFra: () => void;
  enCours: boolean;
}

function DispoLigne({ d }: { d: ContactUrgence['dispo'] }) {
  const Icone = d.k === 'ok' ? Check : d.k === 'conflit' ? AlertTriangle : Ban;
  return <span className={`u-dispo u-dispo--${d.k}`}><Icone aria-hidden="true" />{d.t}</span>;
}

function Candidat({ c, etape, parNom, onAppel, onCorriger }: { c: ContactUrgence; etape: CascadeEtape; parNom: string; onAppel: PropsTableau['onAppel']; onCorriger: PropsTableau['onCorriger'] }) {
  const appel = appelDe(etape, c.personne_id);
  const btn = (r: ResultatAppel, libelle: string, accent = false) => (
    <button type="button" id={r === 'pas_de_reponse' ? `u-res-${etape.cle}-${c.personne_id}` : undefined} className={`btn btn-sm${accent ? ' btn-accent' : ''}`} aria-label={`${libelle} : ${c.display_name}`} onClick={() => onAppel(etape.cle, c, r)}>{accent && <Check className="icon-sm icon-inline" aria-hidden="true" />}{libelle}</button>
  );
  return (
    <li className="u-cand">
      <div className="u-cand-l1"><b>{c.display_name}</b><small>{c.poles.join(' · ') || (c.is_externe ? 'Externe' : '')}{c.is_externe && c.poles.length ? ' (externe)' : ''}</small></div>
      <div className="u-cand-l2"><DispoLigne d={c.dispo} />{c.exp_atelier > 0 && <span>a remplacé {c.exp_atelier} fois sur cet atelier</span>}</div>
      {c.telephone && (
        <div className="u-cand-l3">
          <Phone className="icon-sm" aria-hidden="true" /><a href={`tel:${c.telephone.replace(/[\s.]/g, '')}`}>{c.telephone}</a>
          <BoutonCopier texte={c.telephone} libelle="Copier" etiquette={`Copier le numéro de ${c.display_name}`} />
        </div>
      )}
      {!c.telephone && c.email && <div className="u-cand-l3 text-sm text-muted">{c.email}</div>}
      {(appel ? (
        <div className="u-cand-res">
          <span className="u-fait"><Check className="icon-sm" aria-hidden="true" /> Appelé·e à {heure(appel.horodatage)} par {prenom(appel.par_nom || parNom)} : <b>{LIBELLE_RESULTAT[appel.resultat]}</b></span>
          <button type="button" className="u-lien" id={`u-corr-${etape.cle}-${c.personne_id}`} aria-label={`Corriger l'appel : ${c.display_name}`} onClick={() => onCorriger(etape.cle, c.personne_id)}>Corriger</button>
        </div>
      ) : (
        <div className="u-cand-res" role="group" aria-label={`Résultat de l'appel : ${c.display_name}`}>{btn('pas_de_reponse', 'Pas de réponse')}{btn('refuse', 'Refuse')}{btn('accepte', 'Accepte', true)}</div>
      ))}
    </li>
  );
}

function ChampCommentaire({ etape, onCommentaire }: { etape: CascadeEtape; onCommentaire: (texte: string) => void }) {
  const [v, setV] = useState(etape.commentaire ?? '');
  const champ = useRef<HTMLInputElement>(null);
  // Un commentaire venu d'ailleurs (quelqu'un d'autre, actualisation) remplace le texte, sauf pendant qu'on est en train de taper.
  useEffect(() => { if (document.activeElement !== champ.current) setV(etape.commentaire ?? ''); }, [etape.commentaire]);
  return (
    <div className="u-commentaire">
      <input ref={champ} className="form-input--bare" placeholder="Note sur cette étape (facultatif)…" aria-label={`Note sur l'étape « ${LIBELLE_COURT[etape.cle] ?? etape.libelle} »`} value={v}
        onChange={(e) => setV(e.target.value)} onBlur={() => { if ((etape.commentaire ?? '') !== v) onCommentaire(v); }} />
    </div>
  );
}

function Groupe({ etape, index, courante, contacts, indisponible, termine, p }: {
  etape: CascadeEtape; index: number; courante: boolean; contacts: ContactUrgence[] | undefined; indisponible: boolean; termine: boolean; p: PropsTableau;
}) {
  const e = etape;
  const dispo = disponibles(contacts), autres = (contacts ?? []).filter((c) => c.dispo.k !== 'ok');
  const appeles = dispo.filter((c) => appelDe(e, c.personne_id)).length;
  const suivante = p.demande.cascade_etapes?.[index + 1];
  const badge = e.statut === 'en_cours' ? <span className="badge badge-accent"><Play className="icon-sm icon-inline" aria-hidden="true" />En cours</span>
    : e.statut === 'echec' ? <span className="badge badge-plain"><X className="icon-sm icon-inline" aria-hidden="true" />Échec</span>
    : e.statut === 'reussi' ? <span className="badge badge-ok"><Check className="icon-sm icon-inline" aria-hidden="true" />Réussi</span>
    : e.statut === 'saute' ? <span className="badge badge-plain"><SkipForward className="icon-sm icon-inline" aria-hidden="true" />Sautée</span>
    : <span className="badge badge-plain">À venir</span>;
  const liste = (l: ContactUrgence[]) => <ul className="u-cands">{l.map((c) => <Candidat key={c.personne_id} c={c} etape={e} parNom={p.demande.absent_nom ?? ''} onAppel={p.onAppel} onCorriger={p.onCorriger} />)}</ul>;
  const estGroupe = estGroupeDeContacts(e.cle);
  // Une note se saisit sur l'étape en cours, et sur celles déjà commencées ou terminées (tant que l'urgence est en cours).
  const saisie = !termine && (e.statut !== 'a_faire' || courante);
  return (
    <section className="u-groupe" data-statut={e.statut} id={`u-g-${e.cle}`} aria-labelledby={`u-gt-${e.cle}`}>
      <h3 className="u-groupe-t" id={`u-gt-${e.cle}`}><span>{index + 1}. {e.libelle}</span>{badge}</h3>
      {estGroupe && (indisponible ? <p className="u-note">Les contacts n'ont pas pu être chargés : actualisez la page.</p>
        : !contacts ? <div style={{ padding: 'var(--s12)' }}><span className="spinner spinner-sm" /></div>
        : dispo.length === 0 ? <p className="u-note">Personne de disponible dans ce groupe.</p>
        : courante || e.statut === 'en_cours' ? liste(dispo)
        : <details><summary className="u-resume">{e.statut === 'a_faire' ? `${dispo.length} contact${dispo.length > 1 ? 's' : ''} disponible${dispo.length > 1 ? 's' : ''}` : `${appeles} appelé·e·s sur ${dispo.length}`}</summary>{liste(dispo)}</details>)}
      {estGroupe && autres.length > 0 && (
        <details className="u-indispos"><summary>{autres.length} personne{autres.length > 1 ? 's' : ''} indisponible{autres.length > 1 ? 's' : ''} ce jour-là</summary>{liste(autres)}</details>
      )}
      {e.cle === 'admin' && !courante && <p className="u-note">Ces personnes ne sont pas dans l'application : appelez-les directement sur place.</p>}
      {e.cle === 'sur_place' && !courante && e.statut === 'a_faire' && <p className="u-note">Quelqu'un est-il déjà sur place (autre atelier au même endroit) pour fusionner ou co-animer ? Rien à appeler ici.</p>}
      {e.cle === 'escalade' && !courante && <p className="u-note">Si personne n'a accepté, le management décide dans l'heure : annuler, fusionner ou maintenir.</p>}
      {saisie ? <ChampCommentaire key={`${p.demande.id}-${e.cle}`} etape={e} onCommentaire={(t) => p.onCommentaire(e.cle, t)} />
        : e.commentaire ? <p className="u-note" style={{ borderStyle: 'solid' }}><FileText className="icon-sm" aria-hidden="true" />« {e.commentaire} »</p> : null}
      {!termine && courante && suivante && estGroupe && dispo.length > 0 && appeles < dispo.length && (
        <div className="u-passer"><span>Pas la peine d'appeler tout le monde ?</span><button type="button" className="btn btn-sm" onClick={() => p.onPasser('echec')}>Passer à « {LIBELLE_COURT[suivante.cle] ?? suivante.libelle} »</button><button type="button" className="u-lien" onClick={() => p.onPasser('saute')}>Sauter cette étape</button></div>
      )}
    </section>
  );
}

function ProchainContactBandeau({ p, etapes }: { p: PropsTableau; etapes: CascadeEtape[] }) {
  const attente = accepteEnAttente(etapes);
  if (attente) {
    const nom = p.personnes.find((x) => x.id === attente.personneId)?.display_name ?? 'La personne';
    const absent = p.demande.absent_nom ?? 'la personne absente';
    // Quelqu'un que l'application donnait pour indisponible peut accepter quand même : on le rappelle avant de confirmer.
    const dispo = Object.values(p.contexte?.contacts ?? {}).flat().find((c) => c.personne_id === attente.personneId)?.dispo;
    return (
      <section className="u-confirme" role="region" aria-labelledby="u-conf-t">
        <h4 id="u-conf-t" tabIndex={-1}>{nom} remplace {absent} ?</h4>
        <p>L'urgence prend fin : l'atelier « {p.demande.atelier_nom} » est maintenu avec {prenom(nom)} comme remplaçant·e. Vous pouvez consulter la FRA de l'atelier avant de confirmer.</p>
        {dispo && dispo.k !== 'ok' && <p className="u-confirme-dispo"><b>À vérifier avec {prenom(nom)} :</b> <DispoLigne d={dispo} /></p>}
        <div className="u-confirme-a">
          <button type="button" className="btn btn-accent" disabled={p.enCours} onClick={() => p.onConfirmerRemplacement(attente.personneId)}>Confirmer : {prenom(nom)} remplace {prenom(absent)}</button>
          <button type="button" className="btn" disabled={p.enCours} onClick={() => p.onCorriger(attente.cle, attente.personneId)}>Pas finalement</button>
          {p.hasFra && <button type="button" className="u-lien" onClick={p.onFra}>Consulter la FRA</button>}
        </div>
      </section>
    );
  }
  const pr = prochainContact(etapes, p.contexte?.contacts ?? null);
  if (!pr) return null;
  const etape = etapes[pr.index], suivante = etapes[pr.index + 1];
  if (pr.contact) {
    const c = pr.contact;
    return (
      <section className="u-prochain" role="region" aria-labelledby="u-pr-t">
        <div>
          <div className="u-prochain-t" id="u-pr-t" tabIndex={-1}>Prochain contact · {LIBELLE_COURT[etape.cle]}</div>
          <div className="u-prochain-n">{c.display_name}</div>
          <div className="u-prochain-d">{c.poles.join(' · ')}{c.exp_atelier > 0 ? ` · a remplacé ${c.exp_atelier} fois sur cet atelier` : ''} · {c.dispo.t.toLowerCase()}</div>
        </div>
        {c.telephone && (
          <div className="u-prochain-a">
            <a className="btn btn-accent" href={`tel:${c.telephone.replace(/[\s.]/g, '')}`}><Phone className="icon-sm icon-inline" aria-hidden="true" /> Appeler {c.telephone}</a>
            <BoutonCopier texte={c.telephone} libelle="Copier" etiquette={`Copier le numéro de ${c.display_name}`} />
          </div>
        )}
      </section>
    );
  }
  // Pas de contact à appeler dans cette étape : on dit quoi faire, au lieu de laisser un vide.
  let titre: string, texte: string;
  if (etape.cle === 'sur_place') { titre = 'Étape 1 · Relais sur place'; texte = "Quelqu'un est-il déjà sur place (autre atelier au même endroit) pour fusionner ou co-animer ?"; }
  else if (etape.cle === 'admin') { titre = 'Collaborateurs admin sur site'; texte = "Ces personnes ne sont pas dans l'application : appelez-les directement, ou passez à la suite."; }
  else if (etape.cle === 'escalade') { titre = 'Escalade management'; texte = `Le management décide dans l'heure : annuler, fusionner ou maintenir (boutons en bas).`; }
  else { titre = LIBELLE_COURT[etape.cle]; texte = disponibles(p.contexte?.contacts[etape.cle as GroupeContacts]).length ? 'Tous les contacts disponibles de cette étape ont été appelés.' : 'Personne de disponible dans ce groupe.'; }
  return (
    <section className="u-prochain" role="region" aria-labelledby="u-pr-t">
      <div><div className="u-prochain-t" id="u-pr-t" tabIndex={-1}>{titre}</div><div className="u-prochain-d" style={{ fontSize: 'var(--t-13)', color: 'var(--ink-1)' }}>{texte}</div></div>
      <div className="u-prochain-a">
        {etape.cle === 'sur_place' && p.demande.actions_possibles?.includes('urgence_fusion' as Action) && <button type="button" className="btn" onClick={() => p.onIssue('fusion')}>Oui : fusionner</button>}
        {suivante && <button type="button" className="btn btn-accent" disabled={p.enCours} onClick={() => p.onPasser('echec')}>{etape.cle === 'sur_place' ? 'Non : ' : ''}Passer à « {LIBELLE_COURT[suivante.cle] ?? suivante.libelle} »</button>}
      </div>
    </section>
  );
}

function EtapesCote({ etapes, contexte, now, seuil }: { etapes: CascadeEtape[]; contexte: ContexteUrgence | null; now: number; seuil: number }) {
  const [tout, setTout] = useState(false);
  const journal = contexte?.journal ?? [];
  return (
    <>
      <section aria-labelledby="u-et-t">
        <h3 className="u-titre" id="u-et-t">Où en est la cascade</h3>
        <ol className="u-etapes">
          {etapes.map((e, i) => {
            const Icone = e.statut === 'reussi' ? Check : e.statut === 'echec' ? X : e.statut === 'saute' ? SkipForward : e.statut === 'en_cours' ? Play : Circle;
            const libS = { a_faire: 'à venir', en_cours: 'en cours', reussi: 'réussi', echec: 'échec', saute: 'sautée' }[e.statut];
            let meta = e.statut === 'a_faire' ? '' : `${libS}${e.horodatage ? ` · ${heure(e.horodatage)}` : ''}${e.par_nom ? ` · ${prenom(e.par_nom)}` : ''}`;
            let retard = 0;
            if (e.cle === 'escalade' && e.statut === 'en_cours' && e.horodatage) {
              retard = retardEscalade(etapes, now, seuil);
              meta = retard > 0 ? `démarrée à ${heure(e.horodatage)}` : `en cours · décision avant ${heure(new Date(new Date(e.horodatage).getTime() + seuil * 60000).toISOString())}`;
            } else if (e.statut === 'en_cours' && estGroupeDeContacts(e.cle) && contexte) {
              meta += ` · ${(e.contacts ?? []).length}/${disponibles(contexte.contacts[e.cle]).length} appelé·e·s`;
            }
            return (
              <li key={e.cle} className="u-etape" data-statut={e.statut}>
                <span className="u-etape-n">{i + 1}</span>
                <span><span className="u-etape-l"><Icone className="icon-sm icon-inline" aria-hidden="true" /> {LIBELLE_COURT[e.cle] ?? e.libelle}</span>
                  {(meta || retard > 0) && <span className="u-etape-m">{retard > 0 && <b>en retard de {duree(retard)} · </b>}{meta}</span>}</span>
              </li>
            );
          })}
        </ol>
      </section>
      <section aria-labelledby="u-jo-t">
        <h3 className="u-titre" id="u-jo-t">Journal</h3>
        {!contexte ? <span className="spinner spinner-sm" /> : journal.length === 0 ? <p className="u-vide">Rien pour l'instant.</p> : (
          <ul className="u-journal">
            {(tout ? journal : journal.slice(0, 7)).map((j, i) => <li key={i}><time dateTime={j.at}>{heure(j.at)}</time><div><b>{prenom(j.par)}</b> — <span>{j.texte}</span></div></li>)}
          </ul>
        )}
        {journal.length > 7 && <button type="button" className="u-lien" onClick={() => setTout(!tout)}>{tout ? 'Réduire' : `Voir tout (${journal.length})`}</button>}
      </section>
    </>
  );
}

function Fin({ demande }: { demande: Demande }) {
  const [faits, setFaits] = useState<Record<string, boolean>>({});
  if (demande.statut === 'maintenue') {
    const nom = demande.remplacant_nom;
    return (
      <>
        <div className="u-fin"><Check className="icon-sm" aria-hidden="true" /><div><h4>{nom ? `Atelier maintenu : ${nom} remplace ${demande.absent_nom}` : 'Atelier maintenu en présence contenante, sans remplaçant·e'}</h4>
          <p>{nom ? `${nom} n'est pas prévenu·e par l'application : prévenez-le/la directement. La FRA reste consultable depuis la demande.` : "Aucun·e remplaçant·e n'est désigné·e : l'atelier a lieu avec une présence contenante."}</p></div></div>
      </>
    );
  }
  if (demande.statut === 'fusionnee') {
    return (
      <div className="u-fin"><Check className="icon-sm" aria-hidden="true" /><div><h4>Atelier fusionné</h4><p>Deux rappels (rien n'est envoyé ni écrit ailleurs tout seul) :</p>
        <ul style={{ margin: 'var(--s8) 0 0', paddingLeft: 20, fontSize: 'var(--t-13)' }}>
          {[['crm', 'Reporter la fusion dans le CRM (Eveil)'], ['part', 'Prévenir les participant·e·s du nouveau lieu ou horaire']].map(([k, l]) => (
            <li key={k}><label><input type="checkbox" checked={!!faits[k]} onChange={(e) => setFaits({ ...faits, [k]: e.target.checked })} /> {l}</label></li>
          ))}
        </ul></div></div>
    );
  }
  return (
    <div className="u-fin u-fin--annule"><Ban className="icon-sm" aria-hidden="true" /><div><h4>Atelier annulé</h4><p>Les SMS aux participant·e·s sont prêts : envoyez-les maintenant, depuis le bloc « SMS d'annulation à envoyer » juste au-dessus.</p></div></div>
  );
}

export function TableauUrgence(p: PropsTableau) {
  const d = p.demande;
  const etapes = d.cascade_etapes ?? [];
  const enCours = d.statut === 'urgence_cascade';
  const retard = enCours ? retardEscalade(etapes, p.now, p.seuilMinutes) : 0;
  const courante = etapeCourante(etapes);
  const minutes = Math.round((p.now - new Date(d.declaree?.at ?? d.created_at).getTime()) / 60000);
  const par = d.declaree?.par_nom ?? p.contexte?.declare_par_nom ?? null;
  const peut = (a: string) => !!d.actions_possibles?.includes(a as Action);
  const jour = String(d.date_atelier).slice(0, 10);
  return (
    <section className="card u-board" id="u-board" aria-labelledby="u-board-t">
      {p.confirmation && (
        <div className="u-conf" role="status"><Check className="icon-sm" aria-hidden="true" />
          <div><b>Urgence déclarée pour {d.absent_nom}</b> — {d.atelier_nom}, {formatDateLong(jour)}{d.atelier_heure_debut ? ` à ${d.atelier_heure_debut.slice(0, 5).replace(':', ' h ').replace(/ h 00$/, ' h')}` : ''}.{' '}
            {p.confirmation.length > 0 ? `${p.confirmation.join(', ')} ${p.confirmation.length > 1 ? 'sont prévenu·e·s' : 'est prévenu·e'} dans l'application.` : "Aucune autre responsable n'a été prévenue dans l'application : appelez-la."} La cascade démarre ci-dessous.</div></div>
      )}
      {p.distant && enCours && (
        <div className="u-conc" role="status"><Info className="icon-sm" aria-hidden="true" /><span><b>{p.distant.par ?? 'Quelqu’un'}</b> vient de modifier cette urgence. Actualisez pour voir son travail avant de continuer.</span>
          <button type="button" className="btn btn-sm btn-accent" onClick={p.onActualiser}><RefreshCw className="icon-sm icon-inline" aria-hidden="true" />Actualiser</button></div>
      )}
      {p.conflit && enCours && <div className="u-conc" role="alert"><Info className="icon-sm" aria-hidden="true" /><span>{p.conflit}</span></div>}
      {retard > 0 && <div className="u-retard" role="alert"><AlertTriangle className="icon-sm" aria-hidden="true" /><span><b>Délai de décision dépassé de {duree(retard)}.</b> Le management doit trancher : annuler, fusionner ou maintenir.</span></div>}
      <div className="detail-card-header">
        <div>
          <h2 className="detail-card-title" id="u-board-t" tabIndex={-1}><PoleDot couleur={d.pole_couleur} /> {d.atelier_nom}</h2>
          <div className="text-sm text-muted">{formatDateLong(jour)} · {formatHoraire(d.atelier_heure_debut, d.atelier_heure_fin)}{d.atelier_lieu ? ` · ${d.atelier_lieu}` : ''}</div>
        </div>
        <div className="flex gap-8 items-center" style={{ flexWrap: 'wrap' }}>
          <span className="badge badge-err">{d.type === 'urgence_weekend' ? 'Urgence week-end' : 'Urgence semaine'}</span>
          <span className="badge badge-accent"><Clock className="icon-sm icon-inline" aria-hidden="true" />déclarée {ilYa(minutes)}{par ? ` par ${prenom(par)}` : ''}</span>
          <a className="btn btn-sm" href={`#demandes/${d.id}`} target="_blank" rel="noreferrer"><ExternalLink className="icon-sm icon-inline" aria-hidden="true" />Voir la fiche (nouvel onglet)</a>
        </div>
      </div>
      <p className="u-qui"><b>{d.absent_nom}</b> est absent·e.{d.notes ? <small>Notes de l'appel : « {d.notes} » — lisibles par la personne absente et par son/sa remplaçant·e.</small> : null}</p>
      {enCours ? (
        <>
          <ProchainContactBandeau p={p} etapes={etapes} />
          <div className="u-grille">
            <div className="u-groupes">
              {etapes.map((e, i) => <Groupe key={e.cle} etape={e} index={i} courante={courante === i} contacts={estGroupeDeContacts(e.cle) ? p.contexte?.contacts[e.cle] : undefined} indisponible={p.contexteIndisponible} termine={false} p={p} />)}
            </div>
            <aside className="u-cote"><EtapesCote etapes={etapes} contexte={p.contexte} now={p.now} seuil={p.seuilMinutes} /></aside>
          </div>
          <div className="u-issues">
            <span className="u-issues-t">Terminer l'urgence :</span>
            {peut('urgence_fusion') && <button type="button" className="btn" onClick={() => p.onIssue('fusion')}>Atelier fusionné</button>}
            {peut('urgence_maintien') && <button type="button" className="btn" disabled={!p.atelier?.maintien_possible} onClick={() => p.onIssue('maintien')}>Maintenu sans remplaçant·e</button>}
            {peut('urgence_annulation') && <button type="button" className="btn btn-danger" onClick={() => p.onIssue('annulation')}>Annuler + SMS</button>}
            {peut('urgence_maintien') && !p.atelier?.maintien_possible && <p className="u-issues-note">« Maintenu sans remplaçant·e » (présence contenante) n'est pas autorisé pour cet atelier. Pour désigner quelqu'un, cliquez « Accepte » sur sa ligne.</p>}
          </div>
        </>
      ) : (
        <>
          <Fin demande={d} />
          <div className="u-grille"><div /><aside className="u-cote"><EtapesCote etapes={etapes} contexte={p.contexte} now={p.now} seuil={p.seuilMinutes} /></aside></div>
        </>
      )}
    </section>
  );
}

